/**
 * Copyright (C) Siemens AG 1996-2025 ALL RIGHTS RESERVED.
 */

import {Component, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';

import {BaseFormFieldDirective} from '../../base-form-field.directive';
import {AbstractControl, ReactiveFormsModule} from '@angular/forms';
import {FieldLabelComponent} from '../field-label/field-label.component';
import {IxModule} from '@siemens/ix-angular';
import {TranslateModule} from '@ngx-translate/core';
import {TextInputComponent} from '../text-input/text-input.component';
import {takeUntil} from 'rxjs';
import {NormalisedApiError} from '../../../../../../../models/errors/api-error';

@Component({
  selector: 'app-input-list-text',
  imports: [
    CommonModule,
    FieldLabelComponent,
    IxModule,
    ReactiveFormsModule,
    TranslateModule,
    TextInputComponent,
  ],
  templateUrl: './input-list-text.component.html',
  styleUrl: '../input-components.scss',
})
export class InputListTextComponent
  extends BaseFormFieldDirective
  implements OnInit
{
  /** Whether any server validation error targets this list or one of its items. */
  hasError = false;

  override ngOnInit(): void {
    super.ngOnInit();
    this.passportService.onPassportUpdateSuccess
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          this.hasError = false;
          this.control.setErrors(null);
        }
      });
  }

  override onControlValueInitialized() {
    super.onControlValueInitialized();
    if (!this.passData) {
      this.passData = [];
    }
  }

  protected readonly Array = Array;

  protected override clearErrors(): void {
    super.clearErrors();
    this.hasError = false;
  }

  /**
   * Marks the list as invalid when the server returns a validation error targeting
   * this list field or any of its items (e.g. `materialComposition.declaredComponents`
   * or `materialComposition.declaredComponents[0]`).
   *
   * @param normalisedApiError - The normalized error received from the backend.
   * @param control - The form control.
   * @param fullValuePath - The dot-notation path of this list field.
   */
  override processUnprocessableEntityErrors(
    normalisedApiError: NormalisedApiError,
    control: AbstractControl,
    fullValuePath: string,
  ): void {
    // Reset first, then re-evaluate all entries.
    this.hasError = false;
    this.applyListErrors(normalisedApiError, control, fullValuePath);

    // Also let the base class handle errors on individual child fields.
    super.processUnprocessableEntityErrors(
      normalisedApiError,
      control,
      fullValuePath,
    );
  }

  /**
   * Iterates the normalised error entries and sets `hasError` when any entry
   * targets this list directly or one of its items. Extracted to avoid multiple
   * `break` statements in one loop (SonarQube S135).
   */
  private applyListErrors(
    normalisedApiError: NormalisedApiError,
    control: AbstractControl,
    fullValuePath: string,
  ): void {
    for (const entry of normalisedApiError.all) {
      const code = entry.code ?? '';
      const jsonPathField = this.normalizeFieldPath(
        entry.source?.pointer ?? '',
      );

      const isExactMatch = jsonPathField === fullValuePath;
      const isItemMatch =
        jsonPathField.startsWith(`${fullValuePath}[`) ||
        jsonPathField.startsWith(`${fullValuePath}.`);
      const isAncestorMatch =
        fullValuePath.startsWith(jsonPathField) && this.isAttributeRequired();

      if (isExactMatch || isItemMatch || isAncestorMatch) {
        this.hasError = true;
        this.setControlError(control, isItemMatch, isAncestorMatch, code);
        return;
      }
    }
  }

  /**
   * Sets a server error on the control when no error is already present.
   * Item-level matches use ARRAY_CONTAIN_ERROR; ancestor
   * matches use the MISSING_REQUIRED_PROPERTY property error of the ancestor.
   */
  private setControlError(
    control: AbstractControl,
    isItemMatch: boolean,
    isAncestorMatch: boolean,
    code: string,
  ): void {
    if (isItemMatch && !control.errors) {
      control.setErrors({serverError: 'ARRAY_CONTAIN_ERROR'});
    }
    if (isAncestorMatch && !control.errors) {
      control.setErrors({serverError: code});
    }
  }

  addListItem(listItem = '') {
    if (!this.passData) {
      this.passData = [];
    }
    if (Array.isArray(this.passData)) {
      this.passData.push(listItem);
    }
  }

  async removeItem(index: number) {
    if (Array.isArray(this.passData) && this.passData.length > 0) {
      this.passData = this.passData.filter(
        (_: unknown, ind: number) => ind !== index,
      );
    }
    await this.onControlValueChanged(this.passData, this.saveFunction);
  }
}
